import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Bot, MoreVertical, Trash2, Edit, Power, Building2, Filter } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

export default function Agents() {
  const { t } = useTranslation();
  const [agents, setAgents] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [search, setSearch] = useState("");
  const [filterBusiness, setFilterBusiness] = useState("all");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  // Business filter only for admins or multi-business accounts (future pro plan)
  const showBizFilter = user?.role === "admin" || businesses.length > 1;

  const fetchData = async () => {
    try {
      const [agentsRes, bizRes] = await Promise.all([
        api.get("/agents"),
        api.get("/businesses"),
      ]);
      setAgents(agentsRes.data);
      setBusinesses(bizRes.data);
    } catch (e) {
      toast.error(t("agents.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const getBusinessName = (businessId) => {
    const biz = businesses.find(b => b.id === businessId);
    return biz ? biz.name : null;
  };

  const toggleStatus = async (agent) => {
    const newStatus = agent.status === "active" ? "inactive" : "active";
    try {
      await api.put(`/agents/${agent.id}`, { status: newStatus });
      toast.success(`Agent ${newStatus === "active" ? "activated" : "deactivated"}`);
      fetchData();
    } catch (e) {
      toast.error(t("agents.updateFailed"));
    }
  };

  const deleteAgent = async (id) => {
    try {
      await api.delete(`/agents/${id}`);
      toast.success(t("agents.deleted"));
      fetchData();
    } catch (e) {
      toast.error(t("agents.deleteFailed"));
    }
  };

  const filtered = agents.filter(a => {
    const matchesSearch = a.name.toLowerCase().includes(search.toLowerCase());
    const matchesBusiness = filterBusiness === "all" || 
      (filterBusiness === "unassigned" && !a.business_id) ||
      a.business_id === filterBusiness;
    return matchesSearch && matchesBusiness;
  });

  const toneColors = {
    professional: "#0A84FF",
    friendly: "#00E676",
    casual: "#FFD60A",
    formal: "#BF5AF2",
  };

  return (
    <div data-testid="agents-page" className="p-8 animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-['Manrope'] font-extrabold text-foreground">{t("agents.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("agents.subtitle")}</p>
        </div>
        <Button
          data-testid="create-agent-btn"
          onClick={() => navigate("/agents/new")}
          className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(0,230,118,0.3)] active:scale-95 transition-transform duration-150"
        >
          <Plus className="w-4 h-4 mr-2" /> {t("agents.newAgent")}
        </Button>
      </div>

      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="search-agents"
            placeholder={t("agents.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-secondary/50 border-transparent focus:border-primary"
          />
        </div>
        {showBizFilter && (
        <Select value={filterBusiness} onValueChange={setFilterBusiness}>
          <SelectTrigger className="w-[200px] bg-secondary/50 border-transparent">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder={t("agents.filterBusiness")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Businesses</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {businesses.map(b => (
              <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <Bot className="w-16 h-16 text-muted-foreground/30 mb-4" />
          <p className="text-lg font-semibold text-muted-foreground">{t("agents.noAgents")}</p>
          <p className="text-sm text-muted-foreground/60 mt-1">{t("agents.noAgentsSub")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((agent) => (
            <Card
              key={agent.id}
              data-testid={`agent-card-${agent.id}`}
              className="bg-white border-gray-200 hover:border-primary/30 transition-colors duration-300 cursor-pointer group"
              onClick={() => navigate(`/agents/${agent.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{agent.name}</h3>
                      <Badge
                        variant="outline"
                        className="mt-1 text-xs"
                        style={{ borderColor: toneColors[agent.tone] || "#888", color: toneColors[agent.tone] || "#888" }}
                      >
                        {agent.tone}
                      </Badge>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <button className="p-1 rounded hover:bg-white/5 text-muted-foreground" data-testid={`agent-menu-${agent.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-popover border-border">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/agents/${agent.id}`); }}>
                        <Edit className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); toggleStatus(agent); }}>
                        <Power className="w-4 h-4 mr-2" />
                        {agent.status === "active" ? t("agents.deactivate") : t("agents.activate")}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={(e) => { e.stopPropagation(); deleteAgent(agent.id); }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> {t("agents.delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Business Badge */}
                {agent.business_id && (
                  <div className="mb-3">
                    <Badge variant="outline" className="text-xs border-[#BF5AF2]/30 text-[#BF5AF2]">
                      <Building2 className="w-3 h-3 mr-1" />
                      {getBusinessName(agent.business_id) || t("agents.unknown")}
                    </Badge>
                  </div>
                )}

                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>{agent.conversations_count || 0} chats</span>
                  <span>{agent.leads_qualified || 0} leads</span>
                  <span>{agent.appointments_booked || 0} appts</span>
                </div>

                <div className="mt-4 flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${agent.status === "active" ? "bg-primary" : "bg-muted-foreground"}`} />
                  <span className="text-xs text-muted-foreground capitalize">{agent.status}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Stats */}
      {agents.length > 0 && (
        <div className="mt-6 text-sm text-muted-foreground text-center">
          Showing {filtered.length} of {agents.length} agents
        </div>
      )}
    </div>
  );
}
